import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Briefcase } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Briefing = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [empresaId, setEmpresaId] = useState<string>('');
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [produto, setProduto] = useState('');
  const [publico, setPublico] = useState('');
  const [propostaValor, setPropostaValor] = useState('');

  useEffect(() => {
    const loadEmpresa = async () => {
      if (authLoading) return;
      
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        // Check if usuario exists
        const { data: usuario } = await supabase
          .from('usuarios')
          .select('empresa_id, papel')
          .eq('id', user.id)
          .maybeSingle();

        // Admin não precisa preencher briefing de empresa
        if (usuario?.papel === 'admin') {
          navigate('/admin', { replace: true });
          return;
        }

        // Se já tem empresa, carrega os dados dela
        if (usuario?.empresa_id) {
          const { data: empresa } = await supabase
            .from('empresas')
            .select('*')
            .eq('id', usuario.empresa_id)
            .single();

          if (empresa) {
            setEmpresaId(empresa.id);
            setNomeEmpresa(empresa.nome || '');
            setProduto(empresa.produto || '');
            setPublico(empresa.publico || '');
            setPropostaValor(empresa.proposta_valor || '');
          }
        }
        // Se não tem empresa, apenas deixa o formulário vazio
        // A empresa será criada apenas quando o usuário clicar em Salvar
      } catch (error) {
        console.error('Error loading empresa:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEmpresa();
  }, [user, authLoading, navigate]);

  const handleSave = async () => {
    if (!nomeEmpresa || !produto || !publico || !propostaValor) {
      toast({
        title: 'Atenção',
        description: 'Preencha todos os campos',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const { data: fnData, error: fnError } = await supabase.functions.invoke('create-empresa', {
        body: {
          nome: nomeEmpresa,
          produto,
          publico,
          proposta_valor: propostaValor,
        },
      });

      if (fnError || !fnData?.empresa?.id) {
        const detail = (fnData && (fnData.detail || fnData.error)) || fnError?.message;
        throw new Error(detail || 'Não foi possível salvar a empresa');
      }

      setEmpresaId(fnData.empresa.id);

      toast({
        title: 'Sucesso',
        description: 'Informações da empresa salvas!',
      });

      const pendingPlan = localStorage.getItem('pending_plan') || user?.user_metadata?.pending_plan;
      if (pendingPlan && pendingPlan !== 'start') {
        localStorage.removeItem('pending_plan');
        navigate('/upgrade?plan=' + pendingPlan);
      } else {
        localStorage.removeItem('pending_plan');
        navigate('/coordenador');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: 'Erro',
        description: (error as Error)?.message || 'Não foi possível salvar as informações',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Layout title="Briefing da Empresa">
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Informações da Empresa
            </CardTitle>
            <CardDescription>
              Configure as informações que serão usadas nos treinamentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome da Empresa</Label>
              <Input
                id="nome"
                value={nomeEmpresa}
                onChange={(e) => setNomeEmpresa(e.target.value)}
                placeholder="Ex: Tech Solutions"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="produto">Produto/Serviço</Label>
              <Input
                id="produto"
                value={produto}
                onChange={(e) => setProduto(e.target.value)}
                placeholder="Ex: Software de gestão empresarial"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="publico">Público-alvo</Label>
              <Input
                id="publico"
                value={publico}
                onChange={(e) => setPublico(e.target.value)}
                placeholder="Ex: Pequenas e médias empresas"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="proposta">Proposta de Valor</Label>
              <Textarea
                id="proposta"
                value={propostaValor}
                onChange={(e) => setPropostaValor(e.target.value)}
                placeholder="Ex: Automatizar processos e reduzir custos operacionais"
                rows={4}
              />
            </div>

            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full"
              size="lg"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar e Continuar'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Briefing;
